import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faCircle,
  faCircleCheck,
  faSpinner,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";

import { nameOf } from "../../../../utils/functions/ReflectionUtil";
import { CustomInputChangeEvent } from "../../../../utils/models/events/CustomInputChangeEvent";
import { useDeliveryBeforeOrderDialog } from "../dialogs/delivery-before-order-dialog";
import {
  CountryAutoComplete,
  CountryAutoCompleteItem,
} from "../../../components/Input/CountryAutoComplete";
import {
  CustomDateInput,
  CustomInput,
  CustomTextArea,
} from "../../../components/Input/CustomInput";
import { InputLabel } from "../../../components/Input/InputLabel";
import { InputWrapper } from "../../../components/Input/InputWrapper";
import {
  DeliveryTypeOption,
  OrderDeliveryForm,
  PackagingOption,
} from "../useOrderConfirmationEditViewModel";
import { useDeliveryTypeOrderDialog } from "../dialogs/delivery-type-order-dialog";
import { useDeliveryPackagingOrderDialog } from "../dialogs/delivery-packaging-order-dialog";
import {
  Order,
  DeliveryType,
  Packaging,
  OrderDeliveryAddress,
} from "../../../../core/models/entities/Order";
import { Modal } from "../../../components/modal/Modal";
import { DialogFrame } from "../../../components/Dialogs/dialog-base";
import FunctionalList from "../../../components/GenericTable/FunctionalList";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { ColumnSettings } from "../../../components/GenericTable/SubComponents/ListFrame";
import { add } from "lodash";
import { StrucCol } from "../../../components/StructureElements/StrucCol";
import { StrucRow } from "../../../components/StructureElements/StrucRow";
import DeliveryAddressDialog from "./DeliveryAddressDialog";

interface OrderFormProps {
  order: Order;
  form: OrderDeliveryForm;
  onFormChange: (form: OrderDeliveryForm) => void;
  onDeliveryOrPackagingChange?: (
    deliveryType: DeliveryType,
    packaging: Packaging,
  ) => void;
  deliveryTypeOptionList: DeliveryTypeOption[];
  packagingOptionList: PackagingOption[];
  readonly?: boolean;
  validationErrors: string[];
  showValidation: boolean;
}

function isZipCodeSuspicious(zip: string): boolean {
  if (!zip) return false;
  // Generic check: should contain at least 2 alphanumeric characters,
  // can contain spaces or dashes. Length 2-12.
  // Must contain at least one digit to be considered "normal" in most contexts.
  const hasDigit = /\d/.test(zip);
  const validChars = /^[a-zA-Z0-9\s-]{2,12}$/.test(zip);
  return !hasDigit || !validChars;
}

export function OrderForm({
  form,
  order,
  onFormChange,
  onDeliveryOrPackagingChange,
  deliveryTypeOptionList,
  packagingOptionList,
  readonly,
  validationErrors,
  showValidation,
}: OrderFormProps) {
  const { addresses, selectDefaultAddress } = useProfileContext();
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [deliveryType, setDeliveryType] = useState<DeliveryType>(
    form.deliveryType,
  );
  const [packagingType, setPackagingType] = useState<Packaging>(form.packaging);

  // Helper function to check if a field has validation errors
  const hasValidationError = (fieldName: string): boolean => {
    return showValidation && validationErrors.includes(fieldName);
  };

  // Helper function to get error styling
  const getErrorStyle = (fieldName: string) => {
    const baseStyle = { backgroundColor: "#FFFFFF", borderColor: "#ECEEF9" };
    if (hasValidationError(fieldName)) {
      return { ...baseStyle, borderColor: "#EF4444", borderWidth: "2px" };
    }
    return baseStyle;
  };

  function handleFormChange(
    event: React.ChangeEvent<HTMLInputElement> | CustomInputChangeEvent,
  ) {
    const target = event.target;
    const value =
      target.type === "checkbox"
        ? target.checked
        : target.type === "date"
          ? new Date(target.value)
          : target.value;
    const name = target.name;
    //@ts-ignore
    form[name] = value;

    onFormChange({ ...form });
  }

  function onCountryChange(country: CountryAutoCompleteItem): void {
    onFormChange({ ...form, shipToCountry: country.code });
  }

  const deliveryGroup = deliveryTypeOptionList.map((x) => (
    <div
      key={x.key}
      className="flex flex-row items-center pb-4 text-xs"
      onClick={() => !readonly && onDeliveryTypeChange(x.type)}
    >
      {form.deliveryType === x.type ? (
        <FontAwesomeIcon
          icon={faCircleCheck}
          className="text-status-success text-sm"
        />
      ) : (
        <FontAwesomeIcon
          icon={faCircle}
          className="text-white text-sm border rounded-full"
        />
      )}
      <span className="pl-2">{x.label}</span>
    </div>
  ));

  const packagingGroup = packagingOptionList.map((x) => (
    <div
      key={x.key}
      className="flex flex-row items-center pb-4 text-xs"
      onClick={() => !readonly && onPackagingChange(x.type)}
    >
      {form.packaging === x.type ? (
        <FontAwesomeIcon
          icon={faCircleCheck}
          className="text-status-success text-sm"
        />
      ) : (
        <FontAwesomeIcon
          icon={faCircle}
          className="text-white text-sm border rounded-full"
        />
      )}
      <span className="pl-2">{x.label}</span>
    </div>
  ));

  const changeDeliveryOrPackaging = (
    delivery: DeliveryType = deliveryType,
    packaging: Packaging = packagingType,
  ) => onDeliveryOrPackagingChange?.call(null, delivery, packaging);

  const { component: deliveryBeforeComponent, open: deliveryBeforeOpen } =
    useDeliveryBeforeOrderDialog({
      onContinue: async () => {
        onFormChange({ ...form, deliveryBefore12PMwithGLS: true });
      },
      onCancel: () => {},
    });

  const { component: deliveryTypeComponent, open: deliveryTypeOpen } =
    useDeliveryTypeOrderDialog({
      onContinue: async () => {
        onFormChange({ ...form, deliveryType: deliveryType });
        changeDeliveryOrPackaging(deliveryType, packagingType);
      },
      onCancel: () => {
        setDeliveryType(form.deliveryType); // Reset local state on cancel
      },
      type: deliveryTypeOptionList.find((x) => x.key === deliveryType)!,
    });

  const { component: packagingComponent, open: packagingOpen } =
    useDeliveryPackagingOrderDialog({
      onContinue: async () => {
        onFormChange({ ...form, packaging: packagingType });
        changeDeliveryOrPackaging(deliveryType, packagingType);
      },
      onCancel: () => {
        setPackagingType(form.packaging); // Reset local state on cancel
      },
      packaging: packagingOptionList.find((x) => x.key === packagingType)!,
    });

  function onDeliveryBeforeChange(state: boolean) {
    if (!form.deliveryBefore12PMwithGLS && state) {
      deliveryBeforeOpen();
    } else {
      onFormChange({ ...form, deliveryBefore12PMwithGLS: false });
    }
  }

  function onDeliveryTypeChange(delivery: DeliveryType) {
    setDeliveryType(delivery);
    if (delivery == DeliveryType.Standard) {
      onFormChange({ ...form, deliveryType: delivery });
      changeDeliveryOrPackaging(delivery, packagingType);
    } else deliveryTypeOpen();
  }

  function onPackagingChange(packaging: Packaging) {
    setPackagingType(packaging);
    if (packaging == Packaging.NoPackaging) {
      onFormChange({ ...form, packaging: packaging });
      changeDeliveryOrPackaging(deliveryType, packaging);
    } else packagingOpen();
  }

  return (
    <>
      {deliveryBeforeComponent}
      {deliveryTypeComponent}
      {packagingComponent}

      <DeliveryAddressDialog
        isOpen={addressModalOpen}
        setOpen={setAddressModalOpen}
        addresses={addresses}
        onToggleAddress={(address) => selectDefaultAddress(address.id)}
        onSelectAddress={(address) =>
          onFormChange({
            ...form,
            shipToName: address.name,
            shipToAddress: address.address,
            shipToCity: address.city,
            shipToZip: address.zip,
            shipToContactEmail: address.email,
            shipToContactPerson: address.contact,
            shipToContactPhone: address.phone,
            shipToCountry: address.country,
          })
        }
      />

      <div className={"flex bg-[#F8FAFD] p-6"}>
        <div>
          <div className="flex flex-col">
            <div className="py-2 font-bold flex flex-col text-s">
              <span>Delivery</span>
            </div>
            <div className="grid grid-cols-3">
              <div className="pr-4 max-w-48">
                <InputWrapper>
                  <InputLabel>Company</InputLabel>
                  <CustomInput
                    name={nameOf<OrderDeliveryForm>((lf) => lf.shipToName)}
                    value={form.shipToName}
                    onChange={(x) => handleFormChange(x)}
                    style={getErrorStyle("Company")}
                    disabled={readonly}
                  />
                  {hasValidationError("Company") && (
                    <div className="text-red-500 text-xs mt-1">
                      Company is required
                    </div>
                  )}
                </InputWrapper>
              </div>
              <div className="pr-4 max-w-48">
                <InputWrapper>
                  <InputLabel>Contact Person</InputLabel>
                  <CustomInput
                    name={nameOf<OrderDeliveryForm>(
                      (lf) => lf.shipToContactPerson,
                    )}
                    value={form.shipToContactPerson}
                    onChange={(x) => handleFormChange(x)}
                    style={getErrorStyle("Contact Person")}
                    disabled={readonly}
                  />
                  {hasValidationError("Contact Person") && (
                    <div className="text-red-500 text-xs mt-1">
                      Contact Person is required
                    </div>
                  )}
                </InputWrapper>
              </div>
              <div className="pr-4 max-w-48">
                <InputWrapper>
                  <InputLabel>Address</InputLabel>
                  <CustomInput
                    name={nameOf<OrderDeliveryForm>((lf) => lf.shipToAddress)}
                    value={form.shipToAddress}
                    onChange={(x) => handleFormChange(x)}
                    style={getErrorStyle("Address")}
                    disabled={readonly}
                  />
                  {hasValidationError("Address") && (
                    <div className="text-red-500 text-xs mt-1">
                      Address is required
                    </div>
                  )}
                </InputWrapper>
              </div>
              <div className="pr-4 max-w-48">
                <InputWrapper>
                  <InputLabel>
                    Zip Code
                    {!readonly &&
                      form.shipToZip &&
                      isZipCodeSuspicious(form.shipToZip) && (
                        <Tooltip
                          title="The zip code doesn't look like a standard zip code. If it's correct, you can still place the order."
                          placement="top"
                        >
                          <span className="ml-2 cursor-pointer">
                            <FontAwesomeIcon
                              icon={faExclamationTriangle}
                              className="text-yellow-500"
                            />
                          </span>
                        </Tooltip>
                      )}
                  </InputLabel>
                  <CustomInput
                    name={nameOf<OrderDeliveryForm>((lf) => lf.shipToZip)}
                    value={form.shipToZip}
                    onChange={(x) => handleFormChange(x)}
                    style={getErrorStyle("Zip Code")}
                    disabled={readonly}
                  />
                  {hasValidationError("Zip Code") && (
                    <div className="text-red-500 text-xs mt-1">
                      Zip Code is required
                    </div>
                  )}
                </InputWrapper>
              </div>
              <div className="pr-4 max-w-48">
                <InputWrapper>
                  <InputLabel>City</InputLabel>
                  <CustomInput
                    name={nameOf<OrderDeliveryForm>((lf) => lf.shipToCity)}
                    value={form.shipToCity}
                    onChange={(x) => handleFormChange(x)}
                    style={getErrorStyle("City")}
                    disabled={readonly}
                  />
                  {hasValidationError("City") && (
                    <div className="text-red-500 text-xs mt-1">
                      City is required
                    </div>
                  )}
                </InputWrapper>
              </div>
              <div className="pr-4 max-w-48">
                <InputWrapper>
                  <InputLabel>Country</InputLabel>
                  <CountryAutoComplete
                    disabled={readonly ?? false}
                    selectedCode={form.shipToCountry}
                    onCountryChange={onCountryChange}
                  />
                  {hasValidationError("Country") && (
                    <div className="text-red-500 text-xs mt-1">
                      Country is required
                    </div>
                  )}
                </InputWrapper>
              </div>
            </div>
            {!readonly && (
              <em
                className="text-xs font-medium hover:underline cursor-pointer"
                onClick={() => setAddressModalOpen(true)}
              >
                Choose from previous addresses
              </em>
            )}
          </div>
        </div>
        <div>
          <div className="py-2 font-bold block text-xs">
            <p>Est. Shipment Date</p>
          </div>
          <div className="pr-4 max-w-48">
            <InputWrapper>
              <InputLabel>Date</InputLabel>
              {form.isFetchingDate ? (
                <div className="flex items-center justify-center h-10 border border-[#ECEEF9] rounded bg-white">
                  <FontAwesomeIcon
                    icon={faSpinner}
                    spin
                    className="text-gray-400"
                  />
                </div>
              ) : (
                <CustomDateInput
                  min={form.minimumDeliveryDate.toISOString().slice(0, 10)}
                  name={nameOf<OrderDeliveryForm>(
                    (lf) => lf.latestDeliveryDate,
                  )}
                  value={form.latestDeliveryDate.toISOString().slice(0, 10)}
                  onChange={(x) => handleFormChange(x)}
                  disabled={readonly ?? false}
                  style={{ backgroundColor: "#FFFFFF", borderColor: "#ECEEF9" }}
                />
              )}
            </InputWrapper>
          </div>
          <div className="pr-4 max-w-48">
            <div
              className="flex flex-row items-center pb-4 text-xs"
              onClick={() =>
                !readonly &&
                onDeliveryBeforeChange(!form.deliveryBefore12PMwithGLS)
              }
            >
              {form.deliveryBefore12PMwithGLS ? (
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  className="text-status-success text-sm"
                />
              ) : (
                <FontAwesomeIcon
                  icon={faCircle}
                  className="text-white text-sm border rounded-full"
                />
              )}
              <span className="pl-2">Delivery before 12:00 PM with GLS</span>
            </div>
          </div>
        </div>
        <div className="w-32">
          <div className="py-2 font-bold text-xs block">
            <p>Delivery Type</p>
          </div>
          <div className="grid grid-cols-1 pr-4">
            <fieldset id={nameOf<OrderDeliveryForm>((lf) => lf.deliveryType)}>
              {deliveryGroup}
            </fieldset>
          </div>
        </div>
        <div className="w-44">
          <div className="py-2 font-bold text-xs block">
            <p>Packaging</p>
          </div>
          <div className="grid grid-cols-1">
            <fieldset id={nameOf<OrderDeliveryForm>((lf) => lf.packaging)}>
              {packagingGroup}
            </fieldset>
          </div>
        </div>
        <div>
          <div className="py-2 font-bold text-xs block">
            <p>Order Details</p>
          </div>
          <div className="">
            <InputWrapper>
              <InputLabel>Comments</InputLabel>
              <CustomTextArea
                name={nameOf<OrderDeliveryForm>((lf) => lf.orderComment)}
                value={form.orderComment}
                onChange={(x) => handleFormChange(x)}
                disabled={readonly}
                style={{ backgroundColor: "#FFFFFF", borderColor: "#ECEEF9" }}
                className="w-48"
              />
            </InputWrapper>
            {order.lines.some(
              (line) => line.product.productType === "Design",
            ) && (
              <InputWrapper>
                <InputLabel>Where are the clothes sent from?</InputLabel>
                <CustomInput
                  maxLength={30}
                  name={nameOf<OrderDeliveryForm>((lf) => lf.originOfDelivery)}
                  value={form.originOfDelivery}
                  onChange={(x) => handleFormChange(x)}
                  disabled={readonly}
                  style={getErrorStyle("Where are the clothes sent from?")}
                />
                {hasValidationError("Where are the clothes sent from?") && (
                  <div className="text-red-500 text-xs mt-1">
                    Where are the clothes sent from? is required
                  </div>
                )}
              </InputWrapper>
            )}
            <InputWrapper>
              <InputLabel>Requisition Number</InputLabel>
              <CustomInput
                maxLength={30}
                name={nameOf<OrderDeliveryForm>((lf) => lf.requisitionNumber)}
                value={form.requisitionNumber}
                onChange={(x) => handleFormChange(x)}
                disabled={readonly}
                style={getErrorStyle("Requisition Number")}
              />
              {hasValidationError("Requisition Number") && (
                <div className="text-red-500 text-xs mt-1">
                  Requisition Number is required
                </div>
              )}
            </InputWrapper>
          </div>
        </div>
      </div>
    </>
  );
}
